import React, { useEffect, useState } from "react";
import { fetchEpisode } from "../../../../api/peContent";
import type { Lang, Tier } from "../../../../api/serving";
import { peT } from "../../../../i18n/peLesson";

/**
 * Episode — step 2 (Modeling). Mounts the chapter's animated episode for the
 * assignment's tier+lang, resolved from the composite key (never hard-coded).
 * Reuses the established Vimeo embed pattern (PeChapterViewer). Videos are
 * domain-privacy enabled, so the standard player URL works on the allowlisted
 * prod domain. Completion = stepped past (no watch-% enforcement in v1).
 * Does NOT gate on is_published.
 */
const EpisodeStep: React.FC<{ chapterId: string; tier: Tier; lang: Lang }> = ({
  chapterId,
  tier,
  lang,
}) => {
  const t = peT(lang);
  const [vimeoId, setVimeoId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchEpisode(chapterId, tier, lang)
      .then((e) => { if (!cancelled) setVimeoId(e?.vimeoId ?? null); })
      .catch((err) => {
        console.warn("[EpisodeStep] episode load failed:", err);
        if (!cancelled) setVimeoId(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chapterId, tier, lang]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white border border-gray-200 p-8 text-center text-gray-500">
        {t.loadingEpisode}
      </div>
    );
  }

  return (
    <section>
      <header className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">{t.watchTitle}</h2>
        <p className="text-gray-600">{t.watchBlurb}</p>
      </header>
      {vimeoId ? (
        <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-lg">
          <iframe
            src={`https://player.vimeo.com/video/${vimeoId}?autoplay=0&title=0&byline=0&portrait=0`}
            className="w-full h-full"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={t.episodeFrameTitle}
          />
        </div>
      ) : (
        <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
          {t.episodeMissing}
        </div>
      )}
    </section>
  );
};

export default EpisodeStep;
